/* VANTO product page: option-group variant selector + gallery/lightbox. PDP-specific, self-contained. Gate 4. */
(() => {
  const q = (s, c = document) => c.querySelector(s);
  const qa = (s, c = document) => [...c.querySelectorAll(s)];

  /* ---------------- Variant picker ---------------- */
  qa('[data-variant-picker]').forEach((picker) => {
    const form = picker.closest('form');
    if (!form) return;
    const variantsScript = q('[data-product-variants-json]', form);
    if (!variantsScript) return;
    let variants;
    try { variants = JSON.parse(variantsScript.textContent); } catch (e) { return; }

    const groups = qa('.variant-group', picker);
    const idInput = q('[data-product-variant-id]', form);
    /* Activates the hidden field as the real submitting "id" now that JS is confirmed running --
       the <noscript> select carries name="id" instead when JS is unavailable, never both at once. */
    if (idInput) idInput.name = 'id';
    const priceEl = q('[data-product-price]', form.parentElement);
    const submitBtn = q('[data-product-submit]', form);
    const statusEl = q('[data-product-status]', form);
    const waLink = q('[data-whatsapp-template]', form.parentElement);

    const currentSelection = () => groups.map((g) => q('input[data-option-value]:checked', g)?.value ?? null);

    const findVariant = (selection) => variants.find((v) => v.options.length === selection.length && v.options.every((val, i) => val === selection[i]));

    const updateAvailability = (selection) => {
      groups.forEach((g, gi) => {
        qa('input[data-option-value]', g).forEach((input) => {
          const trial = selection.slice();
          trial[gi] = input.value;
          const match = findVariant(trial);
          const stateEl = input.nextElementSibling?.querySelector('[data-option-state]');
          let state = 'available';
          input.removeAttribute('data-sold-out');
          input.removeAttribute('data-unavailable-combo');
          input.disabled = false;
          input.setAttribute('aria-disabled', 'false');
          if (!match) {
            state = 'unavailable';
            input.setAttribute('data-unavailable-combo', '');
            input.disabled = true;
          } else if (!match.available) {
            state = 'sold-out';
            input.setAttribute('data-sold-out', '');
            input.disabled = true;
          }
          if (input.disabled) input.setAttribute('aria-disabled', 'true');
          input.dataset.optionAvailability = state;
          if (stateEl) {
            const stateLabel = state === 'sold-out'
              ? (form.dataset.soldOutLabel || '')
              : state === 'unavailable'
                ? (form.dataset.unavailableLabel || '')
                : (form.dataset.availableLabel || '');
            stateEl.textContent = [input.checked ? form.dataset.selectedLabel : '', stateLabel].filter(Boolean).join('. ');
          }
        });
      });
    };

    const applyVariant = (variant) => {
      const canPurchase = !!variant && variant.available && variant.price > 0;
      if (idInput) idInput.value = variant ? variant.id : '';
      if (priceEl) {
        if (!variant) {
          priceEl.textContent = form.dataset.unavailableLabel || priceEl.textContent;
        } else if (variant.price > 0) {
          priceEl.innerHTML = variant.compareAtFormatted ? `<s class="price__compare-at">${variant.compareAtFormatted}</s> ${variant.priceFormatted}` : variant.priceFormatted;
        } else {
          priceEl.textContent = variant.priceFormatted;
        }
      }
      if (submitBtn) {
        submitBtn.disabled = !canPurchase;
        if (!variant) submitBtn.textContent = form.dataset.unavailableLabel || submitBtn.textContent;
        else if (canPurchase) submitBtn.textContent = form.dataset.addLabel || submitBtn.textContent;
        else if (variant.price === 0) submitBtn.textContent = form.dataset.contactLabel || submitBtn.textContent;
        else submitBtn.textContent = form.dataset.soldOutLabel || submitBtn.textContent;
      }
      if (statusEl) {
        if (!variant) statusEl.textContent = form.dataset.combinationUnavailableNotice || '';
        else if (!variant.available) statusEl.textContent = form.dataset.soldOutNotice || '';
        else if (variant.price === 0) statusEl.textContent = form.dataset.unavailableNotice || '';
        else statusEl.textContent = '';
      }
      if (variant && variant.id) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }
      if (waLink && variant) {
        const message = waLink.dataset.whatsappTemplate.replace('{{variant}}', variant.title || '');
        waLink.href = waLink.dataset.whatsappBase + encodeURIComponent(message);
      }
      form.dispatchEvent(new CustomEvent('vanto:variant-change', { bubbles: true, detail: { variant } }));
    };

    qa('input[data-option-value]', picker).forEach((input) => {
      input.addEventListener('change', () => {
        const selection = currentSelection();
        updateAvailability(selection);
        applyVariant(findVariant(selection));
      });
    });

    /* Initial pass: reflect availability for the server-rendered starting selection (respects ?variant= via selected_or_first_available_variant). */
    updateAvailability(currentSelection());
  });

  /* ---------------- Gallery + lightbox ---------------- */
  qa('[data-product-gallery]').forEach((gallery) => {
    const items = qa('[data-gallery-open]', gallery);
    const thumbs = qa('[data-gallery-thumb]', gallery);
    if (!items.length) return;

    const trackImageReadiness = (item) => {
      const image = q('img', item);
      if (!image) return;
      item.classList.add('is-loading');
      item.setAttribute('aria-busy', 'true');
      const reveal = () => {
        item.classList.remove('is-loading');
        item.removeAttribute('aria-busy');
      };
      const decodeThenReveal = () => {
        if (typeof image.decode === 'function') image.decode().catch(() => {}).finally(reveal);
        else reveal();
      };
      if (image.complete) decodeThenReveal();
      else {
        image.addEventListener('load', decodeThenReveal, { once: true });
        image.addEventListener('error', reveal, { once: true });
      }
    };

    const keepThumbnailVisible = (thumb) => {
      if (!thumb) return;
      const track = thumb.parentElement;
      const trackRect = track.getBoundingClientRect();
      const thumbRect = thumb.getBoundingClientRect();
      if (thumbRect.left >= trackRect.left && thumbRect.right <= trackRect.right) return;
      thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    };

    const showIndex = (index) => {
      items.forEach((el, i) => el.classList.toggle('is-active', i === index));
      thumbs.forEach((el, i) => {
        el.classList.toggle('is-active', i === index);
        el.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
      keepThumbnailVisible(thumbs[index]);
      trackImageReadiness(items[index]);
    };
    trackImageReadiness(items.find((item) => item.classList.contains('is-active')) || items[0]);
    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => showIndex(i)));
    gallery.closest('.product-page')?.addEventListener('vanto:variant-change', (event) => {
      const mediaId = event.detail?.variant?.mediaId;
      if (!mediaId) return;
      const mediaIndex = items.findIndex((item) => item.dataset.mediaId === String(mediaId));
      if (mediaIndex >= 0) showIndex(mediaIndex);
    });

    /* Mobile touch swipe: one media step, clamped at either edge. CSS pan-y keeps
       vertical page scrolling native; a qualifying swipe suppresses only its synthetic click. */
    const main = q('.pdp-gallery__main', gallery);
    if (main && items.length > 1) {
      let gesture = null;
      let suppressClickUntil = 0;

      main.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'touch' || !event.isPrimary || !window.matchMedia('(max-width: 900px)').matches) return;
        if (!event.target.closest('[data-gallery-open].is-active')) return;
        gesture = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
        try { main.setPointerCapture?.(event.pointerId); } catch { /* Pointer capture is an enhancement, not a requirement. */ }
      });

      main.addEventListener('pointerup', (event) => {
        if (!gesture || event.pointerId !== gesture.pointerId) return;
        const deltaX = event.clientX - gesture.x;
        const deltaY = event.clientY - gesture.y;
        const distanceX = Math.abs(deltaX);
        const minDistance = Math.max(48, Math.min(72, main.clientWidth * 0.16));
        const isHorizontalSwipe = distanceX >= minDistance && distanceX > Math.abs(deltaY) * 1.25;
        gesture = null;
        if (!isHorizontalSwipe) return;

        const currentIndex = items.findIndex((item) => item.classList.contains('is-active'));
        const rtl = document.documentElement.dir === 'rtl';
        const direction = rtl ? (deltaX > 0 ? 1 : -1) : (deltaX < 0 ? 1 : -1);
        showIndex(Math.max(0, Math.min(items.length - 1, currentIndex + direction)));
        suppressClickUntil = performance.now() + 500;
      });

      main.addEventListener('pointercancel', () => { gesture = null; });
      main.addEventListener('click', (event) => {
        if (performance.now() > suppressClickUntil) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        suppressClickUntil = 0;
      }, true);
    }

    const lightbox = q('[data-product-lightbox]');
    if (!lightbox) return;
    const stage = q('[data-lightbox-stage]', lightbox);
    const counter = q('[data-lightbox-counter]', lightbox);
    const counterTemplate = counter?.dataset.template || '';
    const prevBtn = q('[data-lightbox-prev]', lightbox);
    const nextBtn = q('[data-lightbox-next]', lightbox);
    if (items.length <= 1) { prevBtn?.setAttribute('hidden', ''); nextBtn?.setAttribute('hidden', ''); }

    let index = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
    let returnFocus = null;

    const render = () => {
      const sourceImg = q('img', items[index]);
      if (!stage || !sourceImg) return;
      stage.innerHTML = '';
      const img = document.createElement('img');
      img.src = sourceImg.currentSrc || sourceImg.src;
      img.alt = sourceImg.alt || '';
      stage.appendChild(img);
      if (counter) counter.textContent = counterTemplate.replace('{{ current }}', index + 1).replace('{{ total }}', items.length);
    };

    const open = (i, origin) => {
      index = i;
      returnFocus = origin;
      render();
      lightbox.setAttribute('aria-hidden', 'false');
      if ('inert' in lightbox) lightbox.inert = false;
      document.body.classList.add('drawer-open');
      requestAnimationFrame(() => q('[data-lightbox-close]', lightbox)?.focus());
    };
    const close = () => {
      lightbox.setAttribute('aria-hidden', 'true');
      if ('inert' in lightbox) lightbox.inert = true;
      document.body.classList.remove('drawer-open');
      returnFocus?.focus({ preventScroll: true });
      returnFocus = null;
    };
    const step = (delta) => {
      if (items.length <= 1) return;
      index = (index + delta + items.length) % items.length;
      render();
    };

    items.forEach((el, i) => el.addEventListener('click', () => open(i, el)));
    qa('[data-lightbox-close]', lightbox).forEach((btn) => btn.addEventListener('click', close));
    prevBtn?.addEventListener('click', () => step(-1));
    nextBtn?.addEventListener('click', () => step(1));

    lightbox.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { close(); return; }
      const rtl = document.documentElement.dir === 'rtl';
      if (e.key === 'ArrowLeft') { step(rtl ? 1 : -1); return; }
      if (e.key === 'ArrowRight') { step(rtl ? -1 : 1); return; }
      if (e.key !== 'Tab') return;
      const focusable = qa('button', lightbox).filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  });

  /* ---------------- Mobile sticky purchase action ---------------- */
  qa('[data-sticky-product-cta]').forEach((sticky) => {
    const productPage = sticky.previousElementSibling?.matches('.product-page')
      ? sticky.previousElementSibling
      : q('.product-page');
    const form = q('.product-form', productPage);
    const originalButton = q('[data-product-submit]', form);
    const originalPrice = q('[data-product-price]', productPage);
    const stickyButton = q('[data-sticky-product-submit]', sticky);
    const stickyPrice = q('[data-sticky-product-price]', sticky);
    if (!form || !originalButton || !stickyButton) return;

    const mobile = window.matchMedia('(max-width: 900px)');
    let passedOriginal = false;

    const setVisible = (visible) => {
      const next = visible && mobile.matches;
      sticky.hidden = !next;
      sticky.setAttribute('aria-hidden', next ? 'false' : 'true');
      document.body.classList.toggle('pdp-sticky-visible', next);
    };

    const syncState = () => {
      stickyButton.disabled = originalButton.disabled;
      stickyButton.textContent = originalButton.textContent;
      if (stickyPrice && originalPrice) stickyPrice.innerHTML = originalPrice.innerHTML;
    };

    const observer = new IntersectionObserver(([entry]) => {
      passedOriginal = !entry.isIntersecting && entry.boundingClientRect.bottom <= 80;
      setVisible(passedOriginal);
    }, { rootMargin: '-80px 0px 0px 0px', threshold: [0, 0.01] });

    observer.observe(originalButton);
    form.addEventListener('vanto:variant-change', syncState);
    new MutationObserver(syncState).observe(originalButton, { attributes: true, childList: true, subtree: true });
    mobile.addEventListener('change', () => setVisible(passedOriginal));
    stickyButton.addEventListener('click', () => {
      if (!originalButton.disabled) originalButton.click();
    });
    syncState();
  });
})();
